/**
 * Client-side dispatch of a term request to `/api/learn/term-request`.
 *
 * Extracted from `LearnSuggestTerm` the moment a second surface needed it: the
 * 404 invitation (`LearnMissCapture`) made the same promise of human review but
 * only ever recorded to PostHog — the exact "remember to go and look" channel
 * the email endpoint exists to replace. One sender means every surface that
 * promises review actually reaches the inbox, with the same delivery semantics.
 *
 * Browser-only (`window.location`): callable from `"use client"` components
 * exclusively, which every render site of a request affordance already is.
 *
 * ## Delivery semantics, shared by every caller
 *
 * Fire-and-forget on purpose, and it must never gate the caller's confirmation
 * state. The visitor's side of the interaction is complete the moment they press
 * the button: the promise made to them is "a human reviews every suggestion",
 * and whether our internal notification left the building is not their problem.
 * Awaiting it would put a network round-trip — and a spinner, and a failure
 * state — in front of an acknowledgement that is already true.
 *
 * `keepalive` because someone who submits and immediately navigates away is the
 * common case; without it the browser cancels the request on unload and the
 * request is lost precisely when someone cared enough to type and leave.
 *
 * The server re-screens the term. Callers send the normalized one anyway so the
 * two sides agree on what was asked, but nothing here is trusted there.
 */
import posthog from "posthog-js";

import type { LearnTermMissSurface } from "@/lib/learn/termProposal";

/**
 * The browser's own PostHog id, so the SERVER-emitted `learn_term_requested`
 * event lands on the same person timeline as every client-side event. The
 * server falls back to an IP hash when this is absent (pre-init, opt-out,
 * blocked script) — a detached event is worth more than a dropped one.
 */
function currentDistinctId(): string | undefined {
  try {
    return posthog.get_distinct_id();
  } catch {
    // benign: PostHog not initialized or blocked; the server attributes the
    // event to an anonymous hash instead.
    return undefined;
  }
}

export function notifyTermRequest(term: string, surface: LearnTermMissSurface) {
  try {
    void fetch("/api/learn/term-request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        term,
        surface,
        requestedFrom: window.location.href,
        distinctId: currentDistinctId(),
      }),
      keepalive: true,
    }).catch(() => {
      // benign: a lost notification costs the nudge and one analytics event,
      // never the visitor's confirmation.
    });
  } catch {
    // benign: same reasoning — never let this break the submission.
  }
}

/**
 * The follow-up address, sent after the suggestion itself has landed.
 *
 * A separate `intent` rather than a second field on the original request,
 * because the box asks for the address on its CONFIRMATION panel — the form
 * stays one line, and only someone who actually suggested something is asked
 * for anything more. The server treats this intent as attach-only: no row is
 * created, no ask is counted, no internal email is sent.
 *
 * Same fire-and-forget semantics as `notifyTermRequest`, and for the same
 * reason: the visitor is told "if we write it, we will let you know", which is
 * a statement about the future rather than a receipt to be confirmed now.
 */
export function notifyMeAboutTerm(term: string, email: string) {
  try {
    void fetch("/api/learn/term-request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        term,
        email,
        intent: "notify-me",
        distinctId: currentDistinctId(),
      }),
      keepalive: true,
    }).catch(() => {
      // benign: a lost address costs one thank-you months from now, never the
      // visitor's confirmation.
    });
  } catch {
    // benign: same reasoning — never let this break the panel.
  }
}
